import { ModalAction } from '../ModalAction'
import { useCoachingString } from '../useCoachingString'
import { getModalDraft, saveModalDraft } from '@/core/api/modalDraft'
import { useAutosave } from '@/core/hooks/useAutosave'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { bewilligungForm } from '../../../templates'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { toastBlocked } from '@/core/lib/toast'
import { AskModal } from '@/core/components/ui/ask-modal'
import type { FieldErrors } from '@/core/lib/fieldError'
import type { FieldId } from '@/core/lib/result'
import { getAt } from '@/core/lib/path'
import { templatePaths } from '@/core/lib/validate'
import type { BewilligungForm } from '../../../lib/extractBewilligung'
import { useSwitchPhase } from '@/core/hooks/usePhaseNav'
import {
  loadBewilligung,
  buildBewilligungReference,
  checkBewilligung,
  checkKostentraeger,
  KOSTENTRAEGER_FIELDS,
  confirmBewilligung,
  adoptBewilligung,
  persistKostentraeger,
  getBewilligungPath,
  type BewilligungCheck,
} from '../../../flows/bewilligung'
import { getParticipant, getCoachingKostentraeger } from '@/core/data/coachings'
import { missingStammdaten } from '@/core/lib/stammdatenComplete'
import { downloadStaffDocument } from '../../../lib/downloadStaffDocument'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { Button, Icon, ScrollBar, toastError, toastSuccess } from 'admination-react'
import { PdfRegion } from '@/core/components/modal/PdfRegion'

const s = (v: unknown) => (v == null ? '' : String(v))

const NON_KOSTENTRAEGER_FIELDS = new Set<FieldId>(
  templatePaths(bewilligungForm).filter((p) => !KOSTENTRAEGER_FIELDS.includes(p)),
)
const KOSTENTRAEGER_MANUAL_FIELDS = new Set<FieldId>(KOSTENTRAEGER_FIELDS)

function mergeFieldErrors(a: FieldErrors | undefined, b: FieldErrors): FieldErrors {
  if (!a || a.size === 0) return b
  if (b.size === 0) return a
  const merged = new Map(a)
  for (const [k, v] of b) merged.set(k, v)
  return merged
}

export function BewilligungDokument({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard } = useApp()
  const switchPhase = useSwitchPhase()
  const scrollerRef = useRef<HTMLElement>(null)
  const [view, setView] = useState<'pruefen' | 'doc'>('pruefen')
  const [values, setValues] = useState<FormValues | null>(null)
  const [extraction, setExtraction] = useState<BewilligungForm | null>(null)
  const [check, setCheck] = useState<BewilligungCheck | null>(null)
  const [busy, setBusy] = useState(false)
  const [ktAttempted, setKtAttempted] = useState(false)
  const [pendingAdopt, setPendingAdopt] = useState<BewilligungCheck | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const finished = useRef(false)

  async function finishClean() {
    if (finished.current) return
    finished.current = true
    if (coachingId) {
      try {
        await flushDraft()
        await confirmBewilligung(coachingId)
      } catch (e) {
        console.error("Bewilligung bestätigen fehlgeschlagen:", e)
        finished.current = false
        if (alive.current) toastError("Bewilligung bestätigen fehlgeschlagen.")
        return
      }
      reloadDashboard()
    }
    if (!alive.current) return
    toastSuccess("Die Bewilligung wurde erfolgreich eingelesen.")
    void switchPhase('ecn')
  }

  async function load(announce: boolean) {
    if (!coachingId) return
    const [stored, reference, ktDefaults, entwurf] = await Promise.all([
      loadBewilligung(coachingId),
      buildBewilligungReference(coachingId),
      getCoachingKostentraeger(coachingId).catch((e) => {
        console.error("Kostenträger-Vorbelegung laden fehlgeschlagen:", e)
        return null
      }),
      getModalDraft<FormValues>(`/bewilligung/${coachingId}/entwurf`),
    ])
    if (!alive.current) return
    const ext = (stored ?? {}) as BewilligungForm
    const init = initialFormValues(bewilligungForm, ext as Record<string, unknown>)
    if (init['Teilnehmer']) init['Teilnehmer']['Vorname'] = reference['Teilnehmer']?.['Vorname'] ?? ''
    if (init['Kostenträger']) {
      const seed = (field: string, dbValue: string | null) => {
        const current = values?.['Kostenträger']?.[field]
        if (typeof current === 'string' && current.trim() !== '') {
          init['Kostenträger'][field] = current
          return
        }
        if (!init['Kostenträger'][field]) init['Kostenträger'][field] = dbValue ?? ''
      }
      seed('Straße', ktDefaults?.strasse ?? null)
      seed('Hausnummer', ktDefaults?.hausnummer ?? null)
      seed('PLZ', ktDefaults?.plz ?? null)
      seed('Ort', ktDefaults?.ort ?? null)
      seed('Zeichen', ktDefaults?.zeichen ?? null)
    }
    const c = checkBewilligung(ext, reference)
    setExtraction(ext)
    setValues({ ...init, Kostenträger: { ...init['Kostenträger'], ...entwurf?.['Kostenträger'] } })
    setCheck(c)
    if (announce) {
      if (c.nameMismatch) {
        toastError("Kein Teilnehmer mit diesem Namen gefunden – bitte korrekte Bewilligung hochladen.")
      } else {
        if (c.ortMismatch) {
          toastError("Durchführungsort weicht von der Organisationsadresse ab – bitte manuell prüfen.")
        }
        if (c.notFound.length > 0) {
          const list = c.notFound.map((x) => `„${x}"`).join(', ')
          toastError(
            c.notFound.length === 1
              ? `${list} nicht gefunden – bitte korrekte Bewilligung hochladen.`
              : `Nicht gefunden: ${list} – bitte korrekte Bewilligung hochladen.`,
          )
        }
      }
    }
  }

  const loadRef = useRef(load)
  loadRef.current = load
  useEffect(() => {
    setView('pruefen')
    loadRef.current(true).catch((e) => {
      console.error("Bewilligung-Prüfung laden fehlgeschlagen:", e)
      if (alive.current) toastError("Bewilligung konnte nicht geladen werden.")
    })
  }, [coachingId])

  const [path] = useCoachingString(coachingId, getBewilligungPath)

  const draft = values ? { Kostenträger: values['Kostenträger'] } : null
  const { flush: flushDraft } = useAutosave(draft, values !== null && !!coachingId && !busy, (value) => {
    if (!coachingId || !value) return
    return saveModalDraft(`/bewilligung/${coachingId}/entwurf`, value)
  }, 250, {
    form: 'bewilligung-pruefung', record: coachingId ?? undefined, label: "Bewilligung",
    recover: (value) => setValues((previous) => previous && value ? { ...previous, Kostenträger: value.Kostenträger } : previous),
  })

  async function advance() {
    if (!check || busy) return
    setKtAttempted(true)
    if (check.nameMismatch) {
      toastBlocked("Kein Teilnehmer mit diesem Namen gefunden – bitte korrekte Bewilligung hochladen.")
      return
    }
    if (check.mismatches.length > 0) {
      if (!canAdopt) {
        toastBlocked(
          "Bewilligung stimmt nicht mit den erfassten Daten überein – bitte übernehmen oder korrekte Bewilligung hochladen.",
        )
        return
      }
      setPendingAdopt(check)
      return
    }
    await finishAdvance(check)
  }

  async function finishAdvance(c: BewilligungCheck) {
    const ktGate = checkKostentraeger(values ?? {})
    if (ktGate.missing.length > 0) {
      toastBlocked("Kostenträger-Daten unvollständig – bitte manuell ergänzen.")
      return
    }
    if (c.notFound.length > 0) {
      toastBlocked(
        "Bewilligung unvollständig gelesen – bitte korrekte Bewilligung hochladen.",
      )
      return
    }
    if (!coachingId) return
    setBusy(true)
    try {
      try {
        const participant = await getParticipant(coachingId)
        if (!alive.current) return
        const missing = missingStammdaten({
          geburtsdatum: participant?.geburtsdatum,
          phone: participant?.phone,
          strasse: participant?.strasse,
          hausnummer: participant?.hausnummer,
          plz: participant?.plz,
          ort: participant?.ort,
        })
        if (missing.length > 0) {
          toastError(
            "Stammdaten noch nicht vollständig — Teilnehmer:in hat die Daten noch nicht ausgefüllt.",
          )
          return
        }
      } catch (e) {
        console.error("Stammdaten-Prüfung fehlgeschlagen:", e)
        if (alive.current) toastError("Stammdaten konnten nicht geprüft werden. Bitte erneut versuchen.")
        return
      }
      try {
        await persistKostentraeger(coachingId, {
          strasse: s(getAt(values, 'Kostenträger.Straße')),
          hausnummer: s(getAt(values, 'Kostenträger.Hausnummer')),
          plz: s(getAt(values, 'Kostenträger.PLZ')),
          ort: s(getAt(values, 'Kostenträger.Ort')),
          zeichen: s(getAt(values, 'Kostenträger.Zeichen')),
        })
      } catch (e) {
        console.error("Kostenträger speichern fehlgeschlagen:", e)
        if (alive.current) toastError("Kostenträger-Daten konnten nicht gespeichert werden.")
        return
      }
      await finishClean()
    } finally {
      if (alive.current) setBusy(false)
    }
  }

  async function doAdopt(): Promise<boolean> {
    if (!check || !extraction || !coachingId) return false
    setBusy(true)
    try {
      await flushDraft()
      const res = await adoptBewilligung(coachingId, extraction, check.adoptable)
      if (!alive.current) return false
      if (!res.ok) {
        toastError(
          "Maßnahme-Nummer aus der Bewilligung ist nicht im Katalog – bitte korrekte Bewilligung hochladen.",
        )
        return false
      }
      await load(false)
      return true
    } catch (e) {
      console.error("Übernahme der Bewilligungsdaten fehlgeschlagen:", e)
      toastError("Übernahme der Bewilligungsdaten fehlgeschlagen.")
      return false
    } finally {
      setBusy(false)
    }
  }

  const hasMismatch = check ? check.mismatches.length > 0 : false
  const wrongPerson = check ? check.nameMismatch : false
  const canAdopt = hasMismatch && !wrongPerson

  const ktErrors = useMemo(() => checkKostentraeger(values ?? {}).errors, [values])
  const mergedErrors = check ? mergeFieldErrors(check.errors, ktErrors) : ktErrors

  function onFieldChange(path: string, value: unknown) {
    if (busy) return
    setValues((prev) => updateFormField(prev, path, value))
  }

  return (
    <>
      <ModalHeader size={view === 'doc' ? 'document' : 'form'} onClose={() => dispatch({ type: 'CLOSE' })} closeDisabled={busy}>
        <div className="pill-cluster">
          {view === 'pruefen' ? (
            <>
              <ModalAction
                icon="i-doc"
                title="Dokument"
                disabled={busy}
                onClick={() => setView('doc')}
              />
              {check && (
                <ModalAction
                  icon="i-check"
                  primaryAction
                  title="Bestätigen"
                  loading={busy}
                  onClick={() => void advance()}
                  disabled={busy}
                />
              )}
            </>
          ) : (
            <>
              <div className="pill">
                <Button type="button" iconOnly title="Herunterladen" onClick={() => downloadStaffDocument(path, coachingId, "Bewilligung")}>
                  <Icon name="i-download" />
                </Button>
                <Button
                  type="button"
                  iconOnly
                  title="Korrigieren"
                  disabled={busy}
                  onClick={() => setView('pruefen')}
                >
                  <Icon name="i-edit" />
                </Button>
              </div>

            </>
          )}
        </div>
      </ModalHeader>
      <AskModal
        open={pendingAdopt !== null}
        icon="i-edit"
        iconStroke
        variant="is-warning"
        question="Bewilligung stimmt nicht mit den erfassten Daten überein – Daten aus der Bewilligung übernehmen?"
        confirmLabel="Übernehmen"
        cancelLabel="Nicht übernehmen"
        busy={busy}
        onConfirm={() => {
          const c = pendingAdopt
          setPendingAdopt(null)
          if (!c) return
          void (async () => {
            const adopted = await doAdopt()
            if (adopted) await finishAdvance(c)
          })()
        }}
        onCancel={() => {
          const c = pendingAdopt
          setPendingAdopt(null)
          if (!c) return
          void finishAdvance(c)
        }}
        onDismiss={() => setPendingAdopt(null)}
      />
      {view === 'pruefen' ? (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            {values ? (
              <fieldset disabled={busy} className="form-contents"><FormRenderer
                  definition={bewilligungForm}
                  values={values}
                  onChange={onFieldChange}
                  errors={mergedErrors}
                  attemptedAdvance={ktAttempted}
                  field={(field) => ({
                    required: KOSTENTRAEGER_MANUAL_FIELDS?.has(field.id),
                    missing: KOSTENTRAEGER_MANUAL_FIELDS?.has(field.id) ? 'mark' : undefined,
                    readOnly: NON_KOSTENTRAEGER_FIELDS?.has(field.id),
                  })}
                /></fieldset>
            ) : null}
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      ) : (
        <PdfRegion
          path={path ?? null}
          waiting={path === undefined}
          loadingLabel="Bewilligung wird geladen …"
          placeholder="Noch keine Bewilligung hochgeladen."
        />
      )}
    </>
  )
}
